import React, { useState } from 'react'
import { 
  Layout, 
  Row, 
  Col, 
  Card, 
  Button, 
  Typography, 
  Select,
  Input,
  Space,
  Spin,
  Empty,
  Breadcrumb,
  Divider,
} from 'antd'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  HomeOutlined, 
  EnvironmentOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { storesApi, siteSettingsApi } from '../../services/api'

const { Content } = Layout
const { Title, Text, Paragraph } = Typography
const { Option } = Select

const StoreLocatorPage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'
  
  // State for filters
  const [region, setRegion] = useState('')
  const [search, setSearch] = useState('')
  const [selectedStore, setSelectedStore] = useState<any>(null)

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch stores with filters
  const { data: storesData, isLoading, error } = useQuery({
    queryKey: ['stores', region, search],
    queryFn: () => storesApi.getAll({
      region,
      search,
      isActive: true
    })
  })

  // Get all stores to extract unique regions
  const { data: allStoresData } = useQuery({
    queryKey: ['allStores'],
    queryFn: () => storesApi.getAll({ isActive: true, limit: 1000 }),
    staleTime: 1000 * 60 * 10, // 10 minutes cache
  })

  const settings = siteSettingsData?.settings

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  // Extract unique regions from stores
  const getUniqueRegions = () => {
    if (!allStoresData?.stores) return []
    const regions = new Set<string>()
    allStoresData.stores.forEach((store: any) => {
      if (store.region?.en) regions.add(store.region.en)
      if (store.region?.zh && currentLang === 'zh') regions.add(store.region.zh)
    })
    return Array.from(regions).sort()
  }

  const regions = getUniqueRegions()

  return (
    <>
      <Helmet>
        <title>{t('storeLocator.title')} - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={getLocalizedText(settings?.pages?.storeLocator?.subtitle, 'Find MedCareHub stores and authorized dealers near you. Get product support, demonstrations, and professional consultation.')} />
        <meta name="keywords" content={`stores, locations, dealers, ${getLocalizedText(settings?.seo?.keywords, 'medical, support')}`} />
      </Helmet>

      <Content>
                 {/* Breadcrumb */}
         <div className="bg-gray-50 py-4">
           <div className="max-w-6xl mx-auto px-4">
             <Breadcrumb
               items={[
                 {
                   title: (
                     <Link to="/">
                       <HomeOutlined /> {t('navigation.home')}
                     </Link>
                   ),
                 },
                 {
                   title: t('storeLocator.title'),
                 },
               ]}
             />
           </div>
         </div>

        {/* Banner Section: video or image (locale-aware) */}
        <section className="relative flex items-center justify-center h-[40vh] text-white">
          {(() => {
            const banner = settings?.pages?.storeLocator?.banner?.[currentLang];
            if (banner?.mediaType === 'video' && banner?.videoUrl) {
              return (
                <video className="absolute inset-0 w-full h-full object-cover" src={banner.videoUrl} autoPlay muted loop playsInline />
              );
            }
            const bgUrl = banner?.imageUrl || settings?.pages?.storeLocator?.bannerImage || '/store-locator-banner.jpg';
            return (
              <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url('${bgUrl}')`, backgroundColor: '#1890ff' }} />
            );
          })()}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
            <Title level={1} className="text-4xl font-bold text-white mb-4 drop-shadow-lg" style={{ color: '#ffffff' }}>
              {getLocalizedText(settings?.pages?.storeLocator?.title) || t('storeLocator.title')}
            </Title>
            <Paragraph className="text-xl text-white max-w-3xl mx-auto drop-shadow-lg">
              {getLocalizedText(settings?.pages?.storeLocator?.subtitle) || t('storeLocator.subtitle')}
            </Paragraph>
          </div>
        </section>

        {/* Header */}
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                {t('storeLocator.filters.title', 'Find Stores')}
              </Title>
            </div>

            {/* Search and Filters */}
            <Row gutter={[16, 16]} className="mb-8">
              <Col xs={24} md={8}>
                <Input
                  placeholder={t('storeLocator.search.placeholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="large"
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={24} md={4}>
                <Select
                  placeholder={t('storeLocator.filters.allRegions')}
                  value={region || undefined}
                  onChange={setRegion}
                  size="large"
                  style={{ width: '100%' }}
                  allowClear
                >
                  {regions.map(r => (
                    <Option key={r} value={r}>{r}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={4}>
                <Button 
                  onClick={() => {
                    setRegion('')
                    setSearch('')
                  }}
                  size="large"
                  style={{ width: '100%' }}
                >
                  {t('storeLocator.filters.clearFilters')}
                </Button>
              </Col>
            </Row>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Store List in 2 Columns */}
          <div className="store-list">
            {isLoading ? (
              <div className="text-center py-20">
                <Spin size="large" />
                <div className="mt-4 text-gray-500">{t('storeLocator.loading')}</div>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <Empty description={t('storeLocator.errors.failedToLoad')} />
              </div>
            ) : !storesData?.stores?.length ? (
              <div className="text-center py-20">
                <Empty description={t('storeLocator.errors.noStoresFound')} />
              </div>
            ) : (
              <Row gutter={[24, 24]}>
                {storesData.stores.map((store: any) => (
                  <Col key={store._id} xs={24} md={12}>
                    <Card 
                      hoverable
                      className={`transition-all duration-200 ${
                        selectedStore?._id === store._id ? 'ring-2 ring-blue-500 shadow-lg' : ''
                      }`}
                      onClick={() => setSelectedStore(store)}
                    >
                      <Space direction="vertical" size="small" className="w-full">
                        <Title level={4} className="mb-2">
                          {getLocalizedText(store.name, 'Store')}
                        </Title>
                        
                        <div className="text-gray-600">
                          <EnvironmentOutlined className="mr-2" />
                          {getLocalizedText(store.address, 'Address not available')}
                        </div>
                        
                        {store.phone && (
                          <div className="text-gray-600">
                            📞 {store.phone}
                          </div>
                        )}
                        
                        {store.region && (
                          <div className="text-sm text-blue-600 font-medium">
                            {getLocalizedText(store.region, '')}
                          </div>
                        )}
                        
                        <Divider className="my-3" />
                        
                        <Space className="w-full justify-between">
                          <Button 
                            type="primary"
                            size="small"
                            icon={<EnvironmentOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              const address = getLocalizedText(store.address, '');
                              window.open(`https://maps.google.com/maps?q=${encodeURIComponent(address)}`, '_blank');
                            }}
                          >
                            {t('storeLocator.results.viewOnMap')}
                          </Button>
                          {store.phone && (
                            <Button 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`tel:${store.phone}`, '_self');
                              }}
                            >
                              {t('storeLocator.results.call')}
                            </Button>
                          )}
                        </Space>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>

          {/* Selected Store Details (without map) */}
          {selectedStore && (
            <div className="mt-8">
              <Card title={t('storeLocator.storeDetails.title')} className="shadow-lg">
                <Row gutter={[24, 24]}>
                  <Col xs={24} md={12}>
                    <Space direction="vertical" size="large" className="w-full">
                      <div>
                        <Title level={3} className="mb-2">
                          {getLocalizedText(selectedStore.name, 'Store')}
                        </Title>
                      </div>

                      {/* Address */}
                      <div>
                        <Title level={5} className="mb-2 flex items-center">
                          <EnvironmentOutlined className="mr-2" />
                          {t('storeLocator.storeDetails.address')}
                        </Title>
                        <Paragraph className="text-gray-600 ml-6">
                          {getLocalizedText(selectedStore.address, 'Address not available')}
                        </Paragraph>
                      </div>

                      {/* Phone */}
                      {selectedStore.phone && (
                        <div>
                          <Title level={5} className="mb-2">
                            📞 {t('storeLocator.storeDetails.phone')}
                          </Title>
                          <Paragraph className="text-gray-600 ml-6">
                            {selectedStore.phone}
                          </Paragraph>
                        </div>
                      )}

                      {/* Region */}
                      {selectedStore.region && (
                        <div>
                          <Title level={5} className="mb-2">
                            🏢 {t('storeLocator.storeDetails.region')}
                          </Title>
                          <Paragraph className="text-gray-600 ml-6">
                            {getLocalizedText(selectedStore.region, '')}
                          </Paragraph>
                        </div>
                      )}
                    </Space>
                  </Col>
                  
                  <Col xs={24} md={12}>
                    <Space direction="vertical" size="large" className="w-full">
                      {/* Action Buttons */}
                      <div>
                        <Title level={5} className="mb-3">{t('storeLocator.storeDetails.quickActions')}</Title>
                        <Space direction="vertical" className="w-full">
                          <Button 
                            type="primary"
                            size="large" 
                            icon={<EnvironmentOutlined />}
                            className="w-full"
                            onClick={() => {
                              const address = getLocalizedText(selectedStore.address, '');
                              window.open(`https://maps.google.com/maps?q=${encodeURIComponent(address)}`, '_blank');
                            }}
                          >
                            {t('storeLocator.storeDetails.openInGoogleMaps')}
                          </Button>
                          
                          {selectedStore.phone && (
                            <Button 
                              size="large"
                              className="w-full"
                              onClick={() => {
                                window.open(`tel:${selectedStore.phone}`, '_self');
                              }}
                            >
                              📞 {t('storeLocator.storeDetails.callStore')}
                            </Button>
                          )}
                        </Space>
                      </div>

                      {/* Additional Info */}
                      <div>
                        <Title level={5} className="mb-3">{t('storeLocator.storeDetails.storeInformation')}</Title>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <Text className="text-gray-600">
                            {t('storeLocator.storeDetails.description')}
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Col>
                </Row>
              </Card>
            </div>
          )}
        </div>

        {/* Additional Info Section */}
        {settings?.stores && (
          <section className="py-16 bg-gray-50">
            <div className="max-w-6xl mx-auto px-4 text-center">
              <Title level={2} className="mb-4">
                {getLocalizedText(settings.stores.title, 'Visit Our Stores')}
              </Title>
              <Paragraph className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                {getLocalizedText(settings.stores.description, 'Experience our products firsthand with expert guidance and personalized consultations.')}
              </Paragraph>
              <Space size="large">
                <Button type="primary" size="large">
                  <Link to="/products">
                    {t('storeLocator.cta.browseProducts')}
                  </Link>
                </Button>
                <Button size="large">
                  <Link to="/support">
                    {t('storeLocator.cta.contactSupport')}
                  </Link>
                </Button>
              </Space>
            </div>
          </section>
        )}
      </Content>
    </>
  )
}

export default StoreLocatorPage
